import { supabase } from './supabase';

export interface Subcategory {
  id: string;
  category_key: string;
  key: string;
  label_en?: string;
  label_ar?: string;
  label_fr?: string;
  parent_id?: string | null;
  slug?: string;
  meta_title?: string;
  meta_description?: string;
  display_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export type SubcategoryInput = Omit<Subcategory, 'id' | 'created_at' | 'updated_at'>;

export interface SubcategoryTreeNode extends Subcategory {
  children: SubcategoryTreeNode[];
  depth: number;
}

export const subcategoriesService = {
  async getActiveSubcategories(categoryKey?: string) {
    let query = supabase
      .from('subcategories')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (categoryKey) {
      query = query.eq('category_key', categoryKey);
    }

    const { data, error } = await query;
    return { data: data as Subcategory[] | null, error };
  },

  async getAllSubcategories() {
    const { data, error } = await supabase
      .from('subcategories')
      .select('*')
      .order('category_key', { ascending: true })
      .order('display_order', { ascending: true });

    return { data: data as Subcategory[] | null, error };
  },

  async createSubcategory(input: SubcategoryInput) {
    const { data, error } = await supabase
      .from('subcategories')
      .insert([input])
      .select()
      .single();

    return { data: data as Subcategory | null, error };
  },

  async updateSubcategory(id: string, updates: Partial<SubcategoryInput>) {
    const { data, error } = await supabase
      .from('subcategories')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    return { data: data as Subcategory | null, error };
  },

  async deleteSubcategory(id: string) {
    const { error } = await supabase
      .from('subcategories')
      .delete()
      .eq('id', id);

    return { error };
  },

  /** Build a nested tree from a flat list of subcategories */
  buildTree(subcategories: Subcategory[]): SubcategoryTreeNode[] {
    const map = new Map<string, SubcategoryTreeNode>();
    const roots: SubcategoryTreeNode[] = [];

    for (const sub of subcategories) {
      map.set(sub.id, { ...sub, children: [], depth: 0 });
    }

    for (const node of map.values()) {
      if (node.parent_id && map.has(node.parent_id)) {
        const parent = map.get(node.parent_id)!;
        node.depth = parent.depth + 1;
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }

    const sortChildren = (nodes: SubcategoryTreeNode[]) => {
      nodes.sort((a, b) => a.display_order - b.display_order);
      for (const node of nodes) {
        sortChildren(node.children);
      }
    };
    sortChildren(roots);

    return roots;
  },

  /** Get all descendant keys (including self) for a given subcategory key */
  getDescendantKeys(subcategories: Subcategory[], rootKey: string): string[] {
    const tree = this.buildTree(subcategories);
    const result: string[] = [];

    const collectAll = (node: SubcategoryTreeNode) => {
      result.push(node.key);
      for (const child of node.children) {
        collectAll(child);
      }
    };

    const search = (nodes: SubcategoryTreeNode[]): boolean => {
      for (const node of nodes) {
        if (node.key === rootKey) {
          collectAll(node);
          return true;
        }
        if (search(node.children)) return true;
      }
      return false;
    };

    for (const node of tree) {
      if (node.key === rootKey) {
        collectAll(node);
        return result;
      }
    }
    search(tree);
    return result;
  },

  /** Flatten tree into a display list with breadcrumb paths */
  flattenTree(nodes: SubcategoryTreeNode[], parentPath = ''): (SubcategoryTreeNode & { path: string })[] {
    const result: (SubcategoryTreeNode & { path: string })[] = [];
    for (const node of nodes) {
      const path = parentPath ? `${parentPath} > ${node.label_en || node.key}` : node.label_en || node.key;
      result.push({ ...node, path });
      result.push(...this.flattenTree(node.children, path));
    }
    return result;
  },

  /** URL segment for a subcategory (slug preferred, key as fallback) */
  getRouteSegment(sub: Pick<Subcategory, 'key' | 'slug'>): string {
    const slug = sub.slug?.trim();
    return slug || sub.key;
  },

  /** Route segments from root to the target subcategory key within a category */
  getPathSegments(
    subcategories: Subcategory[],
    targetKey: string,
    categoryKey?: string,
  ): string[] {
    const scoped = categoryKey
      ? subcategories.filter((s) => s.category_key === categoryKey)
      : subcategories;
    const tree = this.buildTree(scoped);

    const walk = (nodes: SubcategoryTreeNode[], prefix: string[]): string[] | null => {
      for (const node of nodes) {
        const segment = this.getRouteSegment(node);
        const current = [...prefix, segment];
        if (node.key === targetKey) return current;
        const found = walk(node.children, current);
        if (found) return found;
      }
      return null;
    };

    const path = walk(tree, []);
    return path ?? [targetKey];
  },

  /** Resolve a subcategory node from URL path segments */
  resolveNodeFromPath(
    subcategories: Subcategory[],
    categoryKey: string,
    segments: string[],
  ): SubcategoryTreeNode | null {
    if (!segments.length) return null;

    const tree = this.buildTree(
      subcategories.filter((s) => s.category_key === categoryKey),
    );

    let nodes = tree;
    let matched: SubcategoryTreeNode | null = null;

    for (const rawSegment of segments) {
      const segment = decodeURIComponent(rawSegment);
      const node = nodes.find(
        (n) => this.getRouteSegment(n) === segment || n.key === segment,
      );
      if (!node) return null;
      matched = node;
      nodes = node.children;
    }

    return matched;
  },

  /** All routable category URLs for sitemap generation */
  collectSitemapRoutes(
    categoryKeys: string[],
    subcategories: Subcategory[],
  ): string[] {
    const routes: string[] = ['/category/all'];

    for (const categoryKey of categoryKeys) {
      routes.push(`/category/${encodeURIComponent(categoryKey)}`);

      const tree = this.buildTree(
        subcategories.filter(
          (s) => s.category_key === categoryKey && s.is_active,
        ),
      );

      const walk = (nodes: SubcategoryTreeNode[], prefix: string[]) => {
        for (const node of nodes) {
          const segment = this.getRouteSegment(node);
          const path = [...prefix, segment];
          routes.push(
            `/category/${encodeURIComponent(categoryKey)}/${path.map(encodeURIComponent).join('/')}`,
          );
          walk(node.children, path);
        }
      };

      walk(tree, []);
    }

    return routes;
  },
};